import { ListObjectsV2Command, S3Client } from '@aws-sdk/client-s3';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { useCallback, useEffect, useRef, useState } from 'react';

const INITIAL_BATCH_SIZE = 24;
const INCREMENTAL_BATCH_SIZE = 18;
const PREFETCH_BATCH_SIZE = 10;
const LOAD_AHEAD_PX = 1800;

const cloudFrontBaseUrl = process.env.REACT_APP_AWS_CLOUD_FRONT_DOMAIN?.replace(/\/$/, '') || '';
const manifestUrl = process.env.REACT_APP_ALBUM_MANIFEST_URL || '/album-manifest.json';
const legacyBucketName = process.env.REACT_APP_AWS_BUCKET_NAME || 'wedding-af';

const client =
  process.env.REACT_APP_AWS_REGION && process.env.REACT_APP_AWS_IDENTITY_POOL_ID
    ? new S3Client({
        region: process.env.REACT_APP_AWS_REGION,
        credentials: fromCognitoIdentityPool({
          clientConfig: { region: process.env.REACT_APP_AWS_REGION },
          identityPoolId: process.env.REACT_APP_AWS_IDENTITY_POOL_ID,
        }),
      })
    : null;

const prefetchedImages = new Set();

const buildAssetUrl = (value) => {
  if (!value) {
    return '';
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  if (!cloudFrontBaseUrl) {
    return value;
  }

  return `${cloudFrontBaseUrl}/${value.replace(/^\/+/, '')}`;
};

const createImageRecord = (item, index) => {
  const key = item?.key || item?.src || item?.thumbSrc || item?.hdSrc || `image-${index}`;
  const src = buildAssetUrl(item?.src || item?.key);
  const thumbSrc = buildAssetUrl(item?.thumbSrc || item?.src || item?.key);
  const hdSrc = buildAssetUrl(item?.hdSrc);

  if (!src) {
    return null;
  }

  const width = Number(item?.width) || null;
  const height = Number(item?.height) || null;

  return {
    key,
    src,
    thumbSrc: thumbSrc || src,
    hdSrc: hdSrc || '',
    width,
    height,
    alt: item?.alt || `Foto ${index + 1}`,
  };
};

const normalizeManifest = (payload) => {
  const rawImages = Array.isArray(payload) ? payload : payload?.images;

  if (!Array.isArray(rawImages)) {
    return [];
  }

  return rawImages.map(createImageRecord).filter(Boolean);
};

const loadManifest = async () => {
  const response = await fetch(manifestUrl, { cache: 'no-cache' });

  if (!response.ok) {
    throw new Error(`Manifest request failed with status ${response.status}`);
  }

  const payload = await response.json();
  return {
    images: normalizeManifest(payload),
    raw: payload,
  };
};

const loadLegacyS3Images = async () => {
  if (!client) {
    throw new Error('Album manifest unavailable and legacy S3 fallback is not configured.');
  }

  let continuationToken;
  const keys = [];

  do {
    const response = await client.send(
      new ListObjectsV2Command({
        Bucket: legacyBucketName,
        ContinuationToken: continuationToken,
      })
    );

    const pageKeys =
      response.Contents?.map((item) => item.Key).filter((key) => /\.(avif|gif|jpe?g|png|webp)$/i.test(key)) || [];

    keys.push(...pageKeys);
    continuationToken = response.IsTruncated ? response.NextContinuationToken : undefined;
  } while (continuationToken);

  return keys.map((key, index) => createImageRecord({ key }, index)).filter(Boolean);
};

const prefetchImage = (src) => {
  if (!src || prefetchedImages.has(src) || typeof window === 'undefined') {
    return;
  }

  const image = new Image();
  image.decoding = 'async';
  image.src = src;
  prefetchedImages.add(src);
};

export const useAlbumGallery = () => {
  const [images, setImages] = useState([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sentinelRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const sessionCacheKey = `albumManifest:${manifestUrl}:${cloudFrontBaseUrl || 'relative'}`;

    const hydrateImages = async () => {
      setLoading(true);
      setError('');

      try {
        const cachedManifest = sessionStorage.getItem(sessionCacheKey);

        if (cachedManifest) {
          const parsedPayload = JSON.parse(cachedManifest);
          const parsedManifest = normalizeManifest(parsedPayload);

          if (parsedManifest.length > 0 && isMounted) {
            setImages(parsedManifest);
            setLoading(false);
          }
        }

        const { images: manifestImages, raw: manifestPayload } = await loadManifest();

        if (manifestImages.length === 0) {
          throw new Error('Album manifest is empty.');
        }

        sessionStorage.setItem(sessionCacheKey, JSON.stringify(manifestPayload));

        if (isMounted) {
          setImages(manifestImages);
          setVisibleCount(INITIAL_BATCH_SIZE);
          setLoading(false);
        }
      } catch (manifestError) {
        try {
          const fallbackImages = await loadLegacyS3Images();

          if (fallbackImages.length === 0) {
            throw new Error('No album images were returned by the fallback source.');
          }

          if (isMounted) {
            setImages(fallbackImages);
            setVisibleCount(INITIAL_BATCH_SIZE);
            setLoading(false);
            setError('Manifest non disponibile: album caricato in modalita legacy.');
          }
        } catch (fallbackError) {
          if (isMounted) {
            setImages([]);
            setLoading(false);
            setError(fallbackError.message || manifestError.message || 'Unable to load the album.');
          }
        }
      }
    };

    hydrateImages();

    return () => {
      isMounted = false;
    };
  }, []);

  const hasMore = visibleCount < images.length;
  const visibleImages = images.slice(0, visibleCount);
  const loadMoreImages = useCallback(() => {
    setVisibleCount((currentVisibleCount) => Math.min(currentVisibleCount + INCREMENTAL_BATCH_SIZE, images.length));
  }, [images.length]);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreImages();
        }
      },
      {
        rootMargin: `${LOAD_AHEAD_PX}px 0px`,
      }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadMoreImages, visibleCount]);

  useEffect(() => {
    if (!hasMore || typeof window === 'undefined') {
      return undefined;
    }

    const handleScroll = () => {
      const remainingScroll =
        document.documentElement.scrollHeight - (window.innerHeight + window.scrollY);

      if (remainingScroll < LOAD_AHEAD_PX) {
        loadMoreImages();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasMore, loadMoreImages]);

  useEffect(() => {
    if (loading) {
      return;
    }

    images.slice(visibleCount, visibleCount + PREFETCH_BATCH_SIZE).forEach((image) => {
      prefetchImage(image.thumbSrc);
    });
  }, [images, loading, visibleCount]);

  return {
    error,
    hasMore,
    images,
    loading,
    sentinelRef,
    slides: images.map((image) => ({
      src: image.src,
      hdSrc: image.hdSrc || undefined,
      alt: image.alt,
      width: image.width || undefined,
      height: image.height || undefined,
    })),
    visibleImages,
  };
};

export const prefetchAlbumImage = prefetchImage;
